import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sparkles, Flame } from 'lucide-react';
import { EventCountdownBanner } from '../components/EventCountdownBanner';
import { ProductCarousel } from '../components/ProductCarousel';
import { TrustStrip } from '../components/TrustStrip';
import { PageMeta, OrganisationSchema } from '../components/SEO';
import { logger } from '../utils/logger';
import { toast } from 'sonner';
import {
  HeroSection,
  ShopByCategory,
  FeaturedBrands,
  LoyaltyCTA,
  EventsShowcase,
  EmailSignup,
} from '../components/HomeSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [homepageBrands, setHomepageBrands] = useState([]);
  const [events, setEvents] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, catImgRes, hpBrandsRes, newArrivalsRes, bestsellersRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/homepage/category-images`).catch(() => ({ data: {} })),
          axios.get(`${API}/brands/featured-homepage?limit=8`).catch(() => ({ data: [] })),
          axios.get(`${API}/homepage/new-arrivals?limit=8`).catch(() => ({ data: [] })),
          axios.get(`${API}/homepage/bestsellers?limit=6`).catch(() => ({ data: [] })),
        ]);
        setEvents(eventsRes.data);
        setCategoryImages(catImgRes.data);
        setHomepageBrands(hpBrandsRes.data);
        setNewArrivals(newArrivalsRes.data);
        setBestsellers(bestsellersRes.data);
      } catch (error) {
        logger.error('Failed to load homepage data:', error);
      }
    };
    fetchData();
  }, []);

  const handleEmailSubscribe = async (e) => {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setEmailSubmitting(true);
    try {
      await axios.post(`${API}/mailing-list/subscribe`, { email: emailInput.trim() });
      toast.success('Welcome to the community!');
      setEmailInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not subscribe. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date());
  const newArrivalsRef = useRef(null);
  const bestsellersRef = useRef(null);

  return (
    <div className="min-h-screen home-cursive">
      <PageMeta description="Independent UK retailer of premium dancewear, activewear, yoga & pilates gear. Curated brands, loyalty rewards, and event shopping." path="/" />
      <OrganisationSchema />

      {upcomingEvents.length > 0 && <EventCountdownBanner event={upcomingEvents[0]} />}
      <HeroSection />
      <TrustStrip />
      <ShopByCategory categoryImages={categoryImages} />

      <ProductCarousel id="new-arrivals" icon={Sparkles} subtitle="Just Dropped" title="New Arrivals" products={newArrivals} scrollRef={newArrivalsRef} viewAllHref="/shop?sort=newest" viewAllLabel="View All New" bg="bg-white" />

      <ProductCarousel id="bestsellers" icon={Flame} subtitle="Most Ordered" title="Best Sellers" products={bestsellers} scrollRef={bestsellersRef} viewAllHref="/shop?sort=popular" viewAllLabel="View All Best Sellers" bg="bg-[#F7F7EB]" />

      <FeaturedBrands brands={homepageBrands} />
      <LoyaltyCTA />
      <EventsShowcase events={events} />
      <EmailSignup emailInput={emailInput} setEmailInput={setEmailInput} emailSubmitting={emailSubmitting} onSubmit={handleEmailSubscribe} />
    </div>
  );
};
